import { useEffect } from '@wordpress/element';

const PromotionWidget = () => {
    useEffect(() => {
        // Global variables to track state
        let lastClickedWidgetType = null;
        let continuousCheckInterval = null;
        let searchObserver = null;

        // This script runs in the Elementor editor window, where `parent` is
        // normally the window itself. When the editor is embedded in a frame
        // from another origin, reading any property of `parent` throws a
        // SecurityError — `typeof` does not guard against that, because the
        // error is raised while evaluating the property access. So probe the
        // parent once and fall back to our own window when it is unreachable.
        const getEditorWindow = () => {
            try {
                if (window.parent && window.parent !== window) {
                    // The access itself is the cross-origin test.
                    void window.parent.document;
                    return window.parent;
                }
            } catch (e) {
                // Cross-origin parent — stay in the current window.
            }

            return window;
        };

        const editorWindow = getEditorWindow();
        const editorDocument = editorWindow.document;

        // Function to customize the promotion dialog
        const customizePromotionDialog = (forceUaeWidget = false) => {
            const dialog = editorDocument.querySelector('#elementor-element--promotion__dialog');
            if (!dialog) return false;
            
            const defaultBtn = dialog.querySelector('.dialog-buttons-action:not(.uae-upgrade-button)');
            if (!defaultBtn) return false;
            
            // Always show our button in search results or if forced
            const shouldShowUaeButton = forceUaeWidget || lastClickedWidgetType === 'uae';
            
            // Clean up any previous custom buttons to avoid duplicates
            const existingCustomBtns = dialog.querySelectorAll('.uae-upgrade-button');
            existingCustomBtns.forEach(btn => btn.remove());
            
            if (shouldShowUaeButton) {
                // Hide the default button
                defaultBtn.style.display = 'none';
                
                // Create our custom button
                const button = document.createElement('a');
                button.textContent = 'Upgrade Now';
                
                // Get widget name from the dialog title
                const dialogTitle = dialog.querySelector('.dialog-header');
                let widgetTitle = 'widget';
                
                if (dialogTitle && dialogTitle.textContent) {
                    widgetTitle = dialogTitle.textContent.trim().toLowerCase().replace(/\s+/g, '-');
                }
                
                // Set href with dynamic widget title in utm_medium
                button.setAttribute('href', `https://ultimateelementor.com/pricing/?utm_source=plugin-editor&utm_medium=${widgetTitle}-promo&utm_campaign=uae-upgrade`);
                button.setAttribute('target', '_blank');
                button.classList.add(
                    'dialog-button', 
                    'dialog-action', 
                    'dialog-buttons-action', 
                    'elementor-button', 
                    'go-pro', 
                    'elementor-button-success', 
                    'uae-upgrade-button'
                );
                
                // Insert our button
                defaultBtn.insertAdjacentElement('afterend', button);
                
                // Add event listener to prevent default behavior
                button.addEventListener('click', (e) => {
                    e.stopPropagation();
                });
                
                // Mark the dialog as customized
                dialog.setAttribute('data-uae-customized', 'true');
                
                return true;
            } else {
                // Not our widget, make sure default button is visible
                defaultBtn.style.display = '';
                dialog.removeAttribute('data-uae-customized');
                return true;
            }
        };
        
        // Function to check if a widget is a UAE widget
        const isUaeWidget = (widget) => {
            if (!widget) return false;
            
            // Check if it has the hfe class in the icon
            const icon = widget.querySelector('.icon > i');
            const hasHfeClass = icon && icon.className.includes('hfe');
            
            // Check if it's in our category
            const isInUaeCategory = widget.closest('#elementor-panel-category-hfe-widgets') !== null;
            
            return hasHfeClass || isInUaeCategory;
        };
        
        // Function to start continuous checking for dialog changes
        const startContinuousCheck = () => {
            // Clear any existing interval
            if (continuousCheckInterval) {
                clearInterval(continuousCheckInterval);
            }
            
            // Set up a new interval to check every 100ms
            continuousCheckInterval = setInterval(() => {
                const dialog = editorDocument.querySelector('#elementor-element--promotion__dialog');
                if (!dialog) return;
                
                if (lastClickedWidgetType === 'uae') {
                    const defaultBtn = dialog.querySelector('.dialog-buttons-action:not(.uae-upgrade-button)');
                    const customBtn = dialog.querySelector('.uae-upgrade-button');
                    
                    // If default button is visible or our button is missing, fix it
                    if ((defaultBtn && defaultBtn.style.display !== 'none') || !customBtn) {
                        customizePromotionDialog(true);
                    }
                }
            }, 100);
            
            // Safety timeout to stop checking after 10 seconds
            setTimeout(() => {
                if (continuousCheckInterval) {
                    clearInterval(continuousCheckInterval);
                    continuousCheckInterval = null;
                }
            }, 10000);
        };
        
        // Own promotion card, shown when Elementor renders no promotion UI at all
        // (its promotions module is disabled while Elementor Pro is active).
        const UAE_FALLBACK_CARD_ID = 'uae-widget-promotion-fallback';

        const removeFallbackCard = () => {
            const existing = editorDocument.getElementById(UAE_FALLBACK_CARD_ID);
            if (existing) {
                existing.remove();
            }
            editorDocument.removeEventListener('mousedown', handleFallbackCardOutsideClick, true);
        };

        const handleFallbackCardOutsideClick = (event) => {
            const card = editorDocument.getElementById(UAE_FALLBACK_CARD_ID);
            if (card && !card.contains(event.target)) {
                removeFallbackCard();
            }
        };

        const showFallbackCard = (detail, upgradeUrl) => {
            removeFallbackCard();

            const widgetTitle = (detail.widgetTitle || '').trim();
            const promoData = (editorWindow.elementor?.config?.v4Promotions || {})[detail.widgetType] || {};
            const title = promoData.title || `${widgetTitle} Widget`.trim();
            const content = promoData.content || `Use ${widgetTitle} widget and other UAE Pro features to extend your toolbox with more control and flexibility.`;

            const doc = editorDocument;
            const card = doc.createElement('div');
            card.id = UAE_FALLBACK_CARD_ID;
            // Mirrors the MUI WidgetPromotionCard rendered by e-react-promotions.
            card.style.cssText = [
                'position:fixed',
                'width:296px',
                'z-index:99999',
                'background:var(--e-a-bg-default, #fff)',
                'color:var(--e-a-color-txt, #515962)',
                'border-radius:8px',
                'box-shadow:0 5px 5px -3px rgba(0,0,0,.2), 0 8px 10px 1px rgba(0,0,0,.14), 0 3px 14px 2px rgba(0,0,0,.12)',
                'font-family:Roboto,Arial,Helvetica,sans-serif',
                'font-size:13px',
                'line-height:1.5',
            ].join(';');

            const header = doc.createElement('div');
            header.style.cssText = 'display:flex;align-items:center;justify-content:space-between;padding:8px 16px 0;';

            const titleEl = doc.createElement('div');
            titleEl.textContent = title;
            titleEl.style.cssText = 'font-weight:600;font-size:14px;line-height:22px;color:var(--e-a-color-txt-accent, #0c0d0e);';

            const closeBtn = doc.createElement('button');
            closeBtn.type = 'button';
            closeBtn.setAttribute('aria-label', 'Close');
            closeBtn.innerHTML = '<i class="eicon-close" aria-hidden="true"></i>';
            closeBtn.style.cssText = 'background:none;border:none;cursor:pointer;font-size:14px;line-height:1;padding:6px;margin-inline-end:-6px;color:var(--e-a-color-txt, #515962);';
            closeBtn.addEventListener('click', removeFallbackCard);

            header.append(titleEl, closeBtn);

            const contentEl = doc.createElement('div');
            contentEl.textContent = content;
            contentEl.style.cssText = 'padding:12px 16px 8px;font-size:13px;color:var(--e-a-color-txt, #515962);';

            const footer = doc.createElement('div');
            footer.style.cssText = 'display:flex;justify-content:flex-end;padding:8px 16px 12px;';

            const cta = doc.createElement('a');
            cta.innerHTML = '<i class="eicon-pro-icon" aria-hidden="true" style="font-size:14px;"></i>';
            cta.appendChild(doc.createTextNode(promoData.ctaText || 'Upgrade Now'));
            cta.setAttribute('href', promoData.ctaUrl || upgradeUrl);
            cta.setAttribute('target', '_blank');
            cta.setAttribute('rel', 'noopener noreferrer');
            cta.style.cssText = 'display:inline-flex;align-items:center;gap:6px;background:#93003f;color:#fff;padding:6px 12px;border-radius:6px;text-decoration:none;font-size:13px;font-weight:500;line-height:1.6;';
            cta.addEventListener('mouseenter', () => { cta.style.background = '#7a0034'; });
            cta.addEventListener('mouseleave', () => { cta.style.background = '#93003f'; });

            footer.appendChild(cta);
            card.append(header, contentEl, footer);
            doc.body.appendChild(card);

            // Position next to the clicked panel widget, clamped to the viewport.
            const rect = detail.target?.getBoundingClientRect?.();
            const win = editorWindow;
            const left = rect ? Math.min(rect.right + 10, win.innerWidth - card.offsetWidth - 10) : 260;
            const top = rect ? Math.min(rect.top, win.innerHeight - card.offsetHeight - 10) : 100;
            card.style.left = `${Math.max(left, 10)}px`;
            card.style.top = `${Math.max(top, 10)}px`;

            editorDocument.addEventListener('mousedown', handleFallbackCardOutsideClick, true);
        };

        // Fallback for Elementor 4.x: the pro-widget promotion renders as a React
        // card (.e-widget-promotion-wrapper) whose CTA comes from the v4Promotions
        // config injected in PHP. If that injection didn't apply, rewrite the CTA
        // link on the mounted card. If no card mounts at all (Elementor Pro active,
        // promotions module disabled), render our own fallback card.
        const handleWidgetPromotionOpen = (event) => {
            const detail = event.detail || {};
            const widgetType = detail.widgetType || '';

            if (!widgetType.startsWith('uael-')) return;

            const slug = (detail.widgetTitle || widgetType.replace('uael-', ''))
                .trim()
                .toLowerCase()
                .replace(/\s+/g, '-');
            const upgradeUrl = `https://ultimateelementor.com/pricing/?utm_source=plugin-editor&utm_medium=${slug}-promo&utm_campaign=uae-upgrade`;

            // Elementor's card mounts synchronously with this event; give it a
            // beat, then take over if nothing rendered.
            setTimeout(() => {
                if (!editorDocument.querySelector('.e-widget-promotion-wrapper')) {
                    showFallbackCard(detail, upgradeUrl);
                }
            }, 150);

            // The card content renders asynchronously after mount — retry briefly.
            const delays = [50, 150, 300, 600, 1200];
            delays.forEach(delay => {
                setTimeout(() => {
                    const wrappers = editorDocument.querySelectorAll('.e-widget-promotion-wrapper');
                    wrappers.forEach(wrapper => {
                        const cta = wrapper.querySelector('a[href*="go.elementor.com"]');
                        if (cta) {
                            cta.setAttribute('href', upgradeUrl);
                        }
                    });
                }, delay);
            });
        };

        // Handle clicks on promotion widgets
        const handleProWidgetClick = (e) => {
            // Find the clicked promotion widget
            let clickedWidget = null;
            const allProWidgets = editorDocument.querySelectorAll('.elementor-element--promotion');
            
            for (let i = 0; i < allProWidgets.length; i++) {
                if (allProWidgets[i].contains(e.target)) {
                    clickedWidget = allProWidgets[i];
                    break;
                }
            }
            
            if (!clickedWidget) return;
            
            // Check if it's our widget
            const isUae = isUaeWidget(clickedWidget);
            
            // Update the last clicked widget type
            lastClickedWidgetType = isUae ? 'uae' : 'other';
            
            // Start continuous checking for dialog changes
            startContinuousCheck();
            
            // Also set up multiple immediate checks with increasing delays
            const delays = [10, 30, 50, 100, 200, 300, 500, 1000, 1500, 2000];
            delays.forEach(delay => {
                setTimeout(() => {
                    const dialog = editorDocument.querySelector('#elementor-element--promotion__dialog');
                    if (dialog) {
                        customizePromotionDialog(isUae);
                    }
                }, delay);
            });
        };
        
        // Create a mutation observer to watch for dialog changes
        const createDialogObserver = () => {
            const observer = new MutationObserver((mutations) => {
                for (const mutation of mutations) {
                    // Look for added nodes that might be the dialog
                    if (mutation.addedNodes.length) {
                        for (const node of mutation.addedNodes) {
                            if (node.id === 'elementor-element--promotion__dialog') {
                                // Dialog was just added, customize it
                                customizePromotionDialog(lastClickedWidgetType === 'uae');
                                startContinuousCheck();
                            }
                        }
                    }
                    
                    // Also check for attribute changes on the dialog
                    if (mutation.type === 'attributes' && 
                        mutation.target.id === 'elementor-element--promotion__dialog') {
                        if (lastClickedWidgetType === 'uae') {
                            customizePromotionDialog(true);
                        }
                    }
                }
                
                // Always check if dialog exists and needs customization
                const dialog = editorDocument.querySelector('#elementor-element--promotion__dialog');
                if (dialog && lastClickedWidgetType === 'uae') {
                    const defaultBtn = dialog.querySelector('.dialog-buttons-action:not(.uae-upgrade-button)');
                    const customBtn = dialog.querySelector('.uae-upgrade-button');
                    
                    // If default button is visible or our button is missing, fix it
                    if ((defaultBtn && defaultBtn.style.display !== 'none') || !customBtn) {
                        customizePromotionDialog(true);
                    }
                }
            });
            
            // Observe the body for changes
            if (editorDocument.body) {
                observer.observe(editorDocument.body, { 
                    childList: true, 
                    subtree: true,
                    attributes: true,
                    attributeFilter: ['style', 'class', 'id']
                });
            }
            
            return observer;
        };
        
        // Special function to handle search results
        const setupSearchResultsHandler = () => {
            // If we already have an observer, disconnect it
            if (searchObserver) {
                searchObserver.disconnect();
                searchObserver = null;
            }
            
            // Create a new observer specifically for search results
            searchObserver = new MutationObserver((mutations) => {
                // Check if search results are visible
                const searchWrapper = editorDocument.querySelector('#elementor-panel-elements-search-wrapper');
                if (!searchWrapper) return;
                
                // Process all promotion widgets in search results
                const searchPromoWidgets = searchWrapper.querySelectorAll('.elementor-element--promotion');
                if (searchPromoWidgets.length === 0) return;
                                
                // Add click handlers to all promotion widgets in search results
                searchPromoWidgets.forEach(widget => {
                    // Remove any existing click handlers
                    widget.removeEventListener('click', handleSearchWidgetClick);
                    
                    // Add our click handler
                    widget.addEventListener('click', handleSearchWidgetClick);
                    
                    // Mark this widget as processed
                    widget.setAttribute('data-uae-processed', 'true');
                });
            });
            
            // Observe the panel for changes
            const panelElements = editorDocument.querySelector('#elementor-panel-elements');
            if (panelElements) {
                searchObserver.observe(panelElements, {
                    childList: true,
                    subtree: true,
                    attributes: false
                });
            }
            
            // Also observe the search input
            const searchInput = editorDocument.querySelector('#elementor-panel-elements-search-input');
            if (searchInput) {
                // Add input event listener
                searchInput.addEventListener('input', handleSearchInput);
            }
        };
        
        // Handle search input changes
        const handleSearchInput = () => {
            // Wait a bit for search results to render
            setTimeout(() => {
                const searchWrapper = editorDocument.querySelector('#elementor-panel-elements-search-wrapper');
                if (!searchWrapper) return;
                
                const searchPromoWidgets = searchWrapper.querySelectorAll('.elementor-element--promotion');
                
                searchPromoWidgets.forEach(widget => {
                    // Remove any existing click handlers
                    widget.removeEventListener('click', handleSearchWidgetClick);
                    
                    // Add our click handler
                    widget.addEventListener('click', handleSearchWidgetClick);
                    
                    // Mark this widget as processed
                    widget.setAttribute('data-uae-processed', 'true');
                });
            }, 300);
        };
        
        // Handle clicks on widgets in search results
        const handleSearchWidgetClick = (e) => {
            
            // Set all widgets in search results to be treated as UAE widgets
            lastClickedWidgetType = 'uae';
            
            // Start continuous checking
            startContinuousCheck();
            
            // Set up immediate checks with increasing delays
            const delays = [10, 30, 50, 100, 200, 300, 500, 1000];
            delays.forEach(delay => {
                setTimeout(() => {
                    const dialog = editorDocument.querySelector('#elementor-element--promotion__dialog');
                    if (dialog) {
                        customizePromotionDialog(true);
                    }
                }, delay);
            });
            
            // Don't stop propagation - we want the dialog to open
        };
        
        // Initialize everything
        const initProWidgets = () => {
            if (!editorDocument) return;
            
            // Remove any existing event listeners to prevent duplicates
            editorDocument.removeEventListener('mousedown', handleProWidgetClick, true);
            editorDocument.removeEventListener('widget-promotion:open', handleWidgetPromotionOpen);

            // Add our event listener with capture phase to ensure it runs first
            editorDocument.addEventListener('mousedown', handleProWidgetClick, true);

            // Elementor 4.x promotion card (dispatched instead of the legacy dialog)
            editorDocument.addEventListener('widget-promotion:open', handleWidgetPromotionOpen);
            
            // Set up search results handler
            setupSearchResultsHandler();
            
            // Create the observer
            const observer = createDialogObserver();
            
            // Check if dialog already exists
            const existingDialog = editorDocument.querySelector('#elementor-element--promotion__dialog');
            if (existingDialog) {
                customizePromotionDialog();
            }
            
            return observer;
        };
        
        // Initialize when Elementor is ready
        let observer = null;
        if (window.elementor) {
            elementor.on('preview:loaded', () => {
                observer = initProWidgets();
            });
        } else {
            window.addEventListener('elementor/frontend/init', () => {
                observer = initProWidgets();
            });
        }
        
        // Cleanup function
        return () => {
            if (continuousCheckInterval) {
                clearInterval(continuousCheckInterval);
            }
            
            if (observer) {
                observer.disconnect();
            }
            
            if (searchObserver) {
                searchObserver.disconnect();
            }
            
            if (editorDocument) {
                editorDocument.removeEventListener('mousedown', handleProWidgetClick, true);
                editorDocument.removeEventListener('widget-promotion:open', handleWidgetPromotionOpen);
                removeFallbackCard();
                
                // Remove search input event listeners
                const searchInput = editorDocument.querySelector('#elementor-panel-elements-search-input');
                if (searchInput) {
                    searchInput.removeEventListener('input', handleSearchInput);
                }
                
                // Remove click handlers from search widgets
                const searchPromoWidgets = editorDocument.querySelectorAll('#elementor-panel-elements-search-wrapper .elementor-element--promotion');
                searchPromoWidgets.forEach(widget => {
                    widget.removeEventListener('click', handleSearchWidgetClick);
                });
            }
            
            if (window.elementor && elementor.off) {
                elementor.off('preview:loaded', initProWidgets);
            } else {
                window.removeEventListener('elementor/frontend/init', initProWidgets);
            }
        };
    }, []);

    return null;
};

export default PromotionWidget;
